import type { Candle, Instrument, Timeframe } from "@trader/shared/MarketData"
import { DISPLAY_TIME_ZONE } from "../../ui/displayTime.ts"
import type { IChartApi, ISeriesApi, SeriesType, UTCTimestamp } from "lightweight-charts"
import { type Drawing, DrawingManager, type DrawingStyle, type Pt } from "lightweight-charts-drawing"
import { isTextTool, textLocation, withText } from "./drawingSettings.ts"
import { type DrawingToolbarState, drawingToolbar } from "./drawingToolbarStore.ts"
import type { DrawingsSnapshot, DrawingsStatus, DrawingsSubscription } from "./drawingsHub.ts"
import { drawingsKey, drawingsStore } from "./drawingsStore.ts"
import { planApply } from "./drawingsSync.ts"

/** TradingView interval codes, used for per-interval drawing visibility. */
const INTERVAL: Readonly<Record<Timeframe, string>> = {
  "1m": "1",
  "3m": "3",
  "5m": "5",
  "15m": "15",
  "1h": "60",
  "4h": "240",
  "1D": "1D"
}

/** What the floating toolbar edits: the first selected drawing (edits apply to every selected one). */
export type SelectedDrawing = Drawing | null

export interface TextEditRequest {
  readonly drawing: Drawing
  /** Where to open the editor, in pixels from the chart's top-left corner. */
  readonly at: Pt
}

const EMPTY: ReadonlyArray<Drawing> = []

type ManagerBar = Candle & { readonly time: UTCTimestamp }

/**
 * The drawings of one chart pane: a DrawingManager that shows the symbol's
 * drawings, kept the same as every other chart of the symbol through the
 * drawings hub (an edit here shows there as it happens, and the other way), and
 * that follows the shared toolbar, arming tools only while its pane is the
 * active one. Plain TypeScript; ChartPane owns its lifetime.
 */
export class PaneDrawings {
  private readonly manager: DrawingManager
  private readonly onTextEdit: (request: TextEditRequest) => void
  private readonly onOpenSettings: (drawing: Drawing) => void
  private readonly onSelection: (selected: SelectedDrawing) => void
  private readonly onStatus: (status: DrawingsStatus) => void
  private instrument: Instrument | null = null
  private active = false
  private subscription: DrawingsSubscription<Drawing> | null = null
  /** The list this pane last applied or published: the base of its next edit. */
  private synced: ReadonlyArray<Drawing> = EMPTY
  /** Set while applying the shared list, whose change events must not be published back. */
  private applying = false
  private status: DrawingsStatus | null = null
  private selectionFrame = 0
  private toolbar: DrawingToolbarState = drawingToolbar.get()
  private readonly cleanups: Array<() => void> = []
  /** The bars as the drawing library wants them, mapped once per change of the chart's bars. */
  private barsCache: { readonly source: ReadonlyArray<Candle>; readonly length: number; readonly last: Candle | undefined; readonly mapped: Array<ManagerBar> } | null = null

  constructor(
    chart: IChartApi,
    series: ISeriesApi<SeriesType>,
    bars: () => ReadonlyArray<Candle>,
    onTextEdit: (request: TextEditRequest) => void,
    onSelection: (selected: SelectedDrawing) => void,
    onStatus: (status: DrawingsStatus) => void,
    onOpenSettings: (drawing: Drawing) => void
  ) {
    this.onTextEdit = onTextEdit
    this.onOpenSettings = onOpenSettings
    this.onSelection = onSelection
    this.onStatus = onStatus
    const managerBars = (): Array<ManagerBar> => {
      const source = bars()
      const cache = this.barsCache
      if (cache !== null && cache.source === source && cache.length === source.length && cache.last === source.at(-1)) {
        return cache.mapped
      }
      // With volume, so volume profiles and anchored VWAP work.
      const mapped = source.map((bar) => ({ ...bar, time: bar.time as UTCTimestamp }))
      this.barsCache = { source, length: source.length, last: source.at(-1), mapped }
      return mapped
    }
    this.manager = new DrawingManager(chart, series, {
      bars: managerBars,
      magnet: this.toolbar.magnet,
      stayInDrawingMode: this.toolbar.stayInDrawingMode,
      timeInfo: () => ({ timeZone: DISPLAY_TIME_ZONE, intraday: true })
    })

    this.cleanups.push(
      this.manager.on("change", (drawings) => {
        this.publish(drawings)
        // A drag changes the drawing on every move: report it once per frame.
        this.scheduleSelectionReport()
      }),
      this.manager.on("selection", () => this.reportSelection()),
      // The library gives its point in the pane, right of a left price scale and below any pane above.
      this.manager.on("textEdit", (drawing, at) => {
        const element = chart.chartElement().getBoundingClientRect()
        const pane = (series.getPane().getHTMLElement() ?? chart.chartElement()).getBoundingClientRect()
        this.onTextEdit({ drawing, at: { x: at.x + chart.priceScale("left").width(), y: at.y + pane.top - element.top } })
      }),
      // Placement done (and not "keep drawing"): the toolbar returns to the cursor.
      this.manager.on("tool", (kind) => {
        if (this.active && kind === null && this.toolbar.tool !== null) drawingToolbar.setTool(null)
      }),
      drawingToolbar.subscribe(() => this.applyToolbar(drawingToolbar.get()))
    )

    // TradingView's double-click on a drawing: type its text right there when it can show
    // some (a shape's text, a line's label), else open its settings. The library handles
    // the text tools itself (its textEdit event) and nothing else.
    const element = chart.chartElement()
    const onDoubleClick = (event: MouseEvent) => {
      if (this.manager.tool() !== null) return
      const id = this.manager.hoveredId() ?? this.manager.selection()[0]
      const drawing = id === undefined ? undefined : this.manager.get(id)
      if (drawing === undefined || isTextTool(drawing)) return
      if (textLocation(drawing) === null) {
        this.onOpenSettings(drawing)
        return
      }
      const box = element.getBoundingClientRect()
      this.onTextEdit({ drawing, at: { x: event.clientX - box.left, y: event.clientY - box.top } })
    }
    element.addEventListener("dblclick", onDoubleClick)
    this.cleanups.push(() => element.removeEventListener("dblclick", onDoubleClick))
  }

  /** Shows a symbol's drawings at a timeframe (and follows them until the symbol changes). */
  show(instrument: Instrument, timeframe: Timeframe): void {
    this.manager.setInterval(INTERVAL[timeframe])
    if (this.instrument?.id === instrument.id) return
    this.instrument = instrument
    this.subscription?.close()
    // The previous symbol's drawings never show (or get saved) on this one while it loads.
    this.applyList(EMPTY)
    this.subscription = drawingsStore.open(drawingsKey(instrument.id), (snapshot) => this.applySnapshot(snapshot))
    this.applySnapshot(this.subscription.current())
  }

  setActive(active: boolean): void {
    this.active = active
    this.manager.setTool(active ? this.toolbar.tool : null)
    // The drawing library deletes its selection on Delete / Backspace in every
    // chart, so only the active chart may hold one.
    if (!active) this.deselect()
  }

  /** Writes the text typed into the inline editor, wherever the drawing keeps it. */
  setText(drawing: Drawing, text: string): void {
    const current = this.manager.get(drawing.id)
    if (current !== undefined) this.manager.update(withText(current, text))
  }

  /** The drawing as it is now (null once removed). */
  current(id: string): Drawing | null {
    return this.manager.get(id) ?? null
  }

  /** Replaces one drawing as a whole (the settings dialog's edits, and its Cancel). */
  replace(drawing: Drawing): void {
    if (this.manager.get(drawing.id) !== undefined) this.manager.update(drawing)
  }

  // --- The floating toolbar's actions, on every selected drawing -----------------

  /** Changes the style (colour, width, line style, …) of the selected drawings. */
  setStyle(patch: Partial<DrawingStyle>): void {
    this.updateSelected((drawing) => ({ ...drawing, style: { ...drawing.style, ...patch } }))
  }

  /** Locks the selected drawings (still selectable, but not movable or deletable), or unlocks them. */
  setLocked(locked: boolean): void {
    this.updateSelected((drawing) => ({ ...drawing, locked }))
  }

  hideSelected(): void {
    for (const id of this.manager.selection()) this.manager.setHidden(id, true)
  }

  removeSelected(): void {
    for (const id of this.manager.selection()) {
      if (!this.manager.get(id)?.locked) this.manager.remove(id)
    }
  }

  /** Copies the selected drawings in place and selects the copies. */
  cloneSelected(): void {
    const copies = this.selectedDrawings().map(({ id: _, ...copy }) => this.manager.add(copy as Drawing))
    this.manager.select(copies)
  }

  bringToFront(): void {
    for (const id of this.manager.selection()) this.manager.bringToFront(id)
  }

  sendToBack(): void {
    for (const id of this.manager.selection()) this.manager.sendToBack(id)
  }

  deselect(): void {
    this.manager.select([])
  }

  dispose(): void {
    this.subscription?.close()
    this.subscription = null
    cancelAnimationFrame(this.selectionFrame)
    for (const cleanup of this.cleanups) cleanup()
    this.manager.destroy()
  }

  private selectedDrawings(): Array<Drawing> {
    return this.manager.selection().flatMap((id) => this.manager.get(id) ?? [])
  }

  private updateSelected(change: (drawing: Drawing) => Drawing): void {
    for (const drawing of this.selectedDrawings()) this.manager.update(change(drawing))
  }

  private reportSelection(): void {
    cancelAnimationFrame(this.selectionFrame)
    this.selectionFrame = 0
    this.onSelection(this.selectedDrawings()[0] ?? null)
  }

  private scheduleSelectionReport(): void {
    if (this.selectionFrame !== 0) return
    this.selectionFrame = requestAnimationFrame(() => this.reportSelection())
  }

  /** This pane's edit, to the other charts of the symbol (and the server). */
  private publish(drawings: ReadonlyArray<Drawing>): void {
    if (this.applying || this.subscription === null) return
    const snapshot = this.subscription.publish(this.synced, drawings)
    // When the hub merged in edits from elsewhere, the merged list comes back as a snapshot.
    if (snapshot.drawings === drawings) this.synced = drawings
  }

  private applySnapshot(snapshot: DrawingsSnapshot<Drawing>): void {
    if (snapshot.status !== this.status) {
      this.status = snapshot.status
      this.onStatus(snapshot.status)
    }
    this.applyList(snapshot.drawings)
  }

  /**
   * Shows the shared list, changing only what changed: drawings edited
   * elsewhere are updated in place, so this pane's selection and any placement
   * or drag in progress survive; added, removed, or reordered ones reload the
   * list (keeping the selection).
   */
  private applyList(next: ReadonlyArray<Drawing>): void {
    const plan = planApply(this.manager.drawings(), next)
    this.applying = true
    try {
      if (plan.kind === "update") {
        for (const drawing of plan.changed) this.manager.update(drawing)
      } else if (plan.kind === "replace") {
        const keep = this.manager.selection()
        this.manager.importJSON(next)
        const ids = new Set(next.map((drawing) => drawing.id))
        const still = keep.filter((id) => ids.has(id))
        if (still.length > 0) this.manager.select(still)
      }
    } finally {
      this.applying = false
      this.synced = next
    }
  }

  private applyToolbar(next: DrawingToolbarState): void {
    const previous = this.toolbar
    this.toolbar = next
    if (next.magnet !== previous.magnet) this.manager.setMagnet(next.magnet)
    if (next.stayInDrawingMode !== previous.stayInDrawingMode) this.manager.setStayInDrawingMode(next.stayInDrawingMode)
    if (next.drawingsVisible !== previous.drawingsVisible) this.manager.setDrawingsVisible(next.drawingsVisible)
    if (next.tool !== previous.tool && this.active) this.manager.setTool(next.tool)
    // "Remove all drawings" acts on the active pane's symbol; the other panes follow through the store.
    if (next.clearRequest !== previous.clearRequest && this.active) this.manager.clear()
  }
}
